import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  EditLoanPurposeDialogData,
  EditLoanPurposeDialogResult,
  LoanPurposeValue,
  YesNoValue,
  getAbn,
} from '@portal-workspace/grow-shared-library';
import { MatButtonModule } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout/flex';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs';
import { LoanPurposeSelectionComponent } from '../loan-purpose-selection-component/loan-purpose-selection.component';
import { YesNoComponent } from '../yes-no-component/yes-no.component';

@UntilDestroy()
@Component({
  templateUrl: './edit-loan-purpose.dialog.html',
  styleUrls: ['./edit-loan-purpose.dialog.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FlexModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    LoanPurposeSelectionComponent,
    YesNoComponent,
  ]
})
export class EditLoanPurposeDialog implements OnInit {

  formGroup: FormGroup<{
    loanPurpose: FormControl<LoanPurposeValue | null>;
    termRenewal: FormControl<YesNoValue | null>;
  }>;

  formControlLoanPurpose: FormControl<LoanPurposeValue | null>;
  formControlTermRenewal: FormControl<YesNoValue | null>;

  isSaving = false;
  isLoading = true;
  showTermRenewal = false;
  hasActiveOverdraftAccount = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EditLoanPurposeDialogData,
    private matDialogRef: MatDialogRef<EditLoanPurposeDialog, EditLoanPurposeDialogResult>,
    private formBuilder: FormBuilder
  ) {
    this.formControlLoanPurpose = formBuilder.control<LoanPurposeValue | null>(null, [Validators.required]);
    this.formControlTermRenewal = formBuilder.control<YesNoValue | null>(null);

    this.formGroup = formBuilder.group({
      loanPurpose: this.formControlLoanPurpose,
      termRenewal: this.formControlTermRenewal,
    });
  }

  ngOnInit(): void {
    // Get the ABN from the application
    const abn = getAbn(this.data.application);
    
    if (!abn) {
      // If no ABN, proceed without checking for active account
      this.isLoading = false;
      this.initializeForm();
      return;
    }

    // Check if there's an active overdraft account
    this.data.checkActiveOverdraftAccountFn(abn).pipe(
      untilDestroyed(this),
      tap({
        next: (response) => {
          this.hasActiveOverdraftAccount = response.payload?.hasActiveAccount === 'active';
          this.isLoading = false;
          this.initializeForm();
        },
        error: (error) => {
          console.error('Failed to check active overdraft account', error);
          this.isLoading = false;
          this.initializeForm();
        }
      })
    ).subscribe();
  }

  private initializeForm(): void {
    const currentLoanPurpose = this.data.application.AppInfo?.LoanPurpose;

    // Initialize with current loan purpose
    if (currentLoanPurpose) {
      this.formControlLoanPurpose.setValue(currentLoanPurpose);
    }

    // Initialize term renewal if applicable
    if (currentLoanPurpose && (currentLoanPurpose.name === 'Limit Increase' || currentLoanPurpose.name === 'Limit Decrease')) {
      const termRenewal = (this.data.application.PricingDetails as any)?.TermRenewal;
      this.showTermRenewal = true;
      this.formControlTermRenewal.setValidators([Validators.required]);
      if (termRenewal === 'Yes' || termRenewal === 'No') {
        this.formControlTermRenewal.setValue(termRenewal === 'Yes');
      }
    }

    // Watch for loan purpose changes to show/hide term renewal
    this.formControlLoanPurpose.valueChanges.pipe(
      untilDestroyed(this),
      tap((loanPurpose: LoanPurposeValue | null) => {
        if (loanPurpose && (loanPurpose.name === 'Limit Increase' || loanPurpose.name === 'Limit Decrease')) {
          this.showTermRenewal = true;
          this.formControlTermRenewal.setValidators([Validators.required]);
        } else {
          this.showTermRenewal = false;
          this.formControlTermRenewal.clearValidators();
          this.formControlTermRenewal.setValue(null);
        }
        this.formControlTermRenewal.updateValueAndValidity();
      })
    ).subscribe();
  }

  onCancel(): void {
    this.matDialogRef.close({ updated: false });
  }

  onSubmit(): void {
    if (this.formGroup.valid && !this.isSaving) {
      this.isSaving = true;

      const loanPurpose = this.formControlLoanPurpose.value!;
      const termRenewal = this.showTermRenewal && this.formControlTermRenewal.value !== null
        ? (this.formControlTermRenewal.value ? 'Yes' : 'No') as 'Yes' | 'No'
        : undefined;

      this.data.updateLoanPurposeFn({
        applicationId: this.data.application.ApplicationId,
        loanPurpose: loanPurpose,
        termRenewal: termRenewal,
      }).pipe(
        untilDestroyed(this),
        tap({
          next: (response) => {
            this.isSaving = false;
            if (response.status) {
              this.matDialogRef.close({ updated: true });
            }
          },
          error: (error) => {
            this.isSaving = false;
            console.error('Failed to update loan purpose', error);
          }
        })
      ).subscribe();
    }
  }
}
